import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Calendar } from "lucide-react";
import BackButton from "@/components/buttons/BackButton";
import useHeaderTitle from "@/hooks/useHeaderTitle";
import { zodResolver } from "@hookform/resolvers/zod";
import { kraManagementSchema } from "@/helpers/validation-schemas/kra-management/kra-management.schema";
import { z } from "zod";
import SelectField from "@/components/form/SelectField";
import { useNavigate } from "react-router-dom";
import { useCreateKRAMutation } from "@/states/kra-management/kra.api";
import TKRA from "@/types/kra-management/TKRA";
import { Toast } from "@/helpers/popups/Toast";
import kraManagementRoute from "@/utils/constants/kra-management/kra-management-route.constant";
import { useErrorHandling } from "@/hooks/useErrorHandling";
import { castTypeArr } from "@/utils/general/cast-type";
import { TFailResponse } from "@/types/general/TFailResponse";
import {
  useGetAllDepartmentQuery,
  useGetAllEmployeeDeparmentQuery,
  useLazyGetAllEmployeeQuery,
} from "@/states/common/common.api";
import { teamManagementSchema } from "@/helpers/validation-schemas/team-management/team-management.schema";
import { useCreateTeamMutation } from "@/states/team-management/team.api";

type schemaType = z.infer<typeof teamManagementSchema>;

// Status options
const statusOptions = [
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

const TeamAddPage: React.FC = () => {
  useHeaderTitle("Add New Team");
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<schemaType>({
    resolver: zodResolver(teamManagementSchema),
  });

  const { data: departmentData } = useGetAllDepartmentQuery();
  const { data: allDepartmentEmployeeData } = useGetAllEmployeeDeparmentQuery();
  console.log("departmentData", departmentData);
  const [
    createTeam,
    { error: createError, isLoading: createLoading, isSuccess },
  ] = useCreateTeamMutation();
  const [
    getAllEmployee,
    {
      data: employeeData,
      error: employeeDataError,
      isLoading: employeeDataLoading,
    },
  ] = useLazyGetAllEmployeeQuery();

  const deptId = watch("dept_id");
  const hodId = watch("hod_id");

  useEffect(() => {
    if (deptId) {
      getAllEmployee(deptId);
    }
  }, [deptId]);
  const onFormSubmit = async (data: schemaType) => {
    try {
      const res = await createTeam(data as any);
      if (res?.data?.success) {
        Toast.fire({
          title: res.data?.message,
          icon: "success",
        });

        navigate(kraManagementRoute.BASE_PATH);
      }
    } catch (err) {
      Toast.fire({
        title: "Fail to create new KRA",
        icon: "error",
      });
    }
  };

  useErrorHandling(...castTypeArr<TFailResponse>([createError]));

  return (
    <div className="mx-auto">
      <BackButton />
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="space-y-6 p-6 bg-white rounded-2xl"
      >
        {/* Date Fields Row */}

        {/* KRA Code and Detail Name Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Status */}
          <div className="space-y-2">
            <label
              htmlFor="dept_id"
              className="block text-sm font-medium text-gray-700"
            >
              Department Name
            </label>
            <SelectField
              name="dept_id"
              control={control}
              options={
                //@ts-ignore
                (departmentData?.data?.map((d: any) => ({
                  label: d.dept_name,
                  value: d.dept_id,
                })) as any) ?? []
              }
              placeholder="Select Department"
            />
            {/* {errors.status && (
              <p className="text-sm text-red-600">{errors.status.message}</p>
            )} */}
          </div>
          <div className="space-y-2">
            <label
              htmlFor="status"
              className="block text-sm font-medium text-gray-700"
            >
              HOD Name
            </label>
            <SelectField
              name="hod_id"
              control={control}
              // employeeData
              options={
                //@ts-ignore
                (allDepartmentEmployeeData?.data?.map((d: any) => ({
                  label: d.employee_name,
                  value: d.user_id,
                })) as any) ?? []
              }
              // placeholder={
              //   employeeDataLoading ? "Loading employees..." : "Select HOD"
              // }
            />
            {errors.hod_id && (
              <p className="text-sm text-red-600">
                {
                  //@ts-ignore
                  errors.hod_id.message
                }
              </p>
            )}
          </div>
          <div className="space-y-2">
            <label
              htmlFor="team_name"
              className="block text-sm font-medium text-gray-700"
            >
              Team Name
            </label>
            <input
              type="text"
              id="team_name"
              {...register("team_name")}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter Team Name"
            />
            {errors.team_name && (
              <p className="text-sm text-red-600">{errors.team_name.message}</p>
            )}
          </div>
        </div>
        <h1>Team Detail</h1>
        {/* Team Members Multi-select */}
        <SelectField
          name="team_members"
          control={control}
          isMulti
          options={
            //@ts-ignore
            (allDepartmentEmployeeData?.data?.map((d: any) => ({
              label: d.employee_name,
              value: d.user_id,
              isDisabled: d.user_id === hodId, // disable only if this is the selected HOD
            })) as any) ?? []
          }
          placeholder={
            employeeDataLoading ? "Loading employees..." : "Select Team Members"
          }
        />
        {errors.team_members && (
          <p className="text-sm text-red-600">
            {
              //@ts-ignore
              errors.team_members.message
            }
          </p>
        )}
        {/* Action Buttons */}
        <div className="flex justify-start space-x-4 pt-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting || createLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamAddPage;
